import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContentMaxPriority,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { useCancelamentoItem } from '@/hooks/useCancelamentoItem';
import { AlertCircle, Clock, XCircle, ChevronLeft } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface CancelarItemModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemId: string;
  pedidoId: string;
  itemNome?: string;
  statusPreSelecionado?: string;
  onSuccess?: () => void;
}

/**
 * Sugestão de motivo pelo que a pessoa escreve — SEM IA, custo zero. (Douglas 04/08)
 *
 * Medido em 2.308 cancelamentos: regra de palavra-chave classifica 50,4% sozinha,
 * e em 695 o motivo marcado não batia com o texto escrito ("cliente desistiu do
 * plano" marcado como Erro de Criação). Corrigir depois é limpeza; sugerir na
 * hora é conserto — o dado nasce certo.
 *
 * Só SUGERE. Quem decide é a pessoa: a regra erra em texto ambíguo, e ninguém
 * deve ser empurrado para um motivo que não é o dele.
 */
const REGRAS_MOTIVO: Array<{ re: RegExp; titulo: string }> = [
  { re: /duplicad|duas vezes|duplicidade/i,                          titulo: 'Pedido Duplicado' },
  { re: /não responde|nao responde|não atende|nao atende|não retorna|nao retorna|sem retorno|sem contato/i, titulo: 'Cliente Não Responde' },
  { re: /desist|não quer mais|nao quer mais/i,                       titulo: 'Cliente Desistiu' },
  { re: /endereç|endereco|complemento/i,                             titulo: 'Endereço Incorreto ou Incompleto' },
  { re: /aparelho|sem estoque/i,                                     titulo: 'Aparelho ou Chip Indisponível' },
  { re: /fidelidade|multa.*operadora/i,                              titulo: 'Cliente com Fidelidade na Operadora Atual' },
  { re: /biometria|não assinou|nao assinou|aceite pendente|bio pendente/i, titulo: 'Contrato Não Assinado / Biometria Não Realizada' },
  { re: /titularidade/i,                                             titulo: 'Titularidade Incorreta' },
  { re: /\bhps?\b.{0,40}(reprovad|negad|n[ãa]o (foi )?liberad)|nenhuma (das )?hp|sem rede|n[ãa]o cabead|fora da abrang/i, titulo: 'HP Negada' },
  { re: /cobertura|viabilidade/i,                                    titulo: 'Sem Cobertura na Área' },
  { re: /oferta melhor|concorren/i,                                  titulo: 'Encontrou Oferta Melhor' },
];

/** Devolve o motivo sugerido, ou null se o texto tiver mais de um sinal (ambíguo). */
function sugerirMotivo(texto: string): string | null {
  if (!texto || texto.trim().length < 8) return null;
  const achados = REGRAS_MOTIVO.filter((r) => r.re.test(texto)).map((r) => r.titulo);
  const unicos = Array.from(new Set(achados));
  return unicos.length === 1 ? unicos[0] : null;
}

export function CancelarItemModal({
  open,
  onOpenChange,
  itemId,
  pedidoId,
  itemNome,
  statusPreSelecionado,
  onSuccess,
}: CancelarItemModalProps) {
  const [step, setStep] = useState(1);
  const [statusSelecionado, setStatusSelecionado] = useState('');
  const [motivosSelecionados, setMotivosSelecionados] = useState<string[]>([]);
  const [justificativa, setJustificativa] = useState('');
  const letrasNaJustificativa = justificativa.replace(/[^a-zA-ZÀ-ÿ]/g, '').length;
  // ⚠️ O botão SÓ libera quando o backend vai aceitar (Douglas 07/08). O modal
  // exigia 10 LETRAS e o backend 20 CARACTERES (`observacao.length < 20` em
  // itensPedido.routes): "dddddddddd" liberava aqui e morria lá com "mínimo 20
  // caracteres" — o consultor confirmava e não cancelava. As DUAS réguas valem:
  // letras (barra "....................") e o comprimento do backend, tal qual.
  const caracteresNaJustificativa = justificativa.length;
  const justificativaLiberada = letrasNaJustificativa >= 10 && caracteresNaJustificativa >= 20;
  const motivoSugerido = sugerirMotivo(justificativa);
  
  const { cancelarItem, isCancelando } = useCancelamentoItem();

  // Status de cancelamento — SÓ os que têm motivo ativo.
  //
  // (Douglas 30/07) "não tem como ter um status sem motivo". Havia 4 status
  // sem nenhum motivo cadastrado: quem escolhia um deles caía num passo 2
  // vazio, e o botão "Próximo" exige ao menos um motivo — beco sem saída, com
  // o BKO voltando e escolhendo o primeiro da lista. Filtrar aqui é a trava
  // que garante que todo cancelamento sai com status E motivo.
  // (Douglas 04/08) "porque cancelar um pedido se ele não é um pedido? ele não
  // foi para pedido fechado." Item em COMERCIAL nunca virou venda — cancelar ali
  // registra a morte de algo que não nasceu e infla o indicador de cancelamento
  // com o que era ajuste de proposta. Eram 58 casos desde junho, e crescendo.
  //
  // O banco também barra (trg_comercial_nao_cancela); aqui é para o usuário ver
  // o caminho certo em vez de bater num erro.
  const { data: quadroDoItem } = useQuery<string | null>({
    queryKey: ['item-quadro-para-cancelar', itemId],
    enabled: open && !!itemId,
    queryFn: async () => {
      const { data } = await supabase
        .from('itens_pedido')
        .select('quadro_atual')
        .eq('id', itemId)
        .maybeSingle();
      return (data?.quadro_atual as string) ?? null;
    },
  });
  const emComercial = quadroDoItem === 'COMERCIAL';

  const { data: statusCancelamento = [] } = useQuery<any[]>({
    queryKey: ['status-cancelamento-com-motivo'],
    queryFn: async () => {
      const [statusRes, motivosRes] = await Promise.all([
        supabase
          .from('status_catalogo')
          .select('id, nome, cor')
          .eq('quadro', 'PEDIDO_CANCELADO')
          .eq('ativo', true)
          .order('ordem'),
        supabase
          .from('motivos_cancelamento')
          .select('status_associado')
          .eq('ativo', true),
      ]);
      const comMotivo = new Set(
        (motivosRes.data || []).map((m) => String(m.status_associado)),
      );
      // (Douglas 03/08) RETENÇÃO não é opção de cancelamento — é estado de
      // ABDICAÇÃO. O pedido chega lá pelo botão "Abdicar", já cancelado e com o
      // motivo real preservado. Oferecer aqui faria o cancelamento nascer com
      // "Consultor Não Vai Recuperar" no lugar do motivo verdadeiro, apagando a
      // causa. O banco também barra (trigger `atualizar_item_ao_cancelar`);
      // aqui é para o usuário nem ver a opção errada.
      const ehRetencao = (nome: string) => /RETEN/i.test(nome || '');
      return (statusRes.data || [])
        .filter((s) => comMotivo.has(String(s.id)))
        .filter((s) => !ehRetencao(String(s.nome)));
    },
    enabled: open,
  });

  // Buscar motivos filtrados por status
  const { data: motivos = [] } = useQuery<any[]>({
    queryKey: ['motivos-por-status', statusSelecionado],
    queryFn: async () => {
      const { data } = await supabase
        .from('motivos_cancelamento')
        .select('*, status:status_catalogo(nome)')
        .eq('status_associado', statusSelecionado)
        .eq('ativo', true)
        .order('ordem');
      return data || [];
    },
    enabled: !!statusSelecionado,
  });

  const motivosDetalhes = motivos.filter((m: any) => motivosSelecionados.includes(m.id));

  // Reset ao abrir/fechar
  useEffect(() => {
    if (!open) {
      setStep(statusPreSelecionado ? 2 : 1);
      setStatusSelecionado(statusPreSelecionado || '');
      setMotivosSelecionados([]);
      setJustificativa('');
    } else if (statusPreSelecionado) {
      setStatusSelecionado(statusPreSelecionado);
      setStep(2);
    }
  }, [open, statusPreSelecionado]);

  // (Douglas 30/07) UM motivo por cancelamento.
  //
  // Isto era checkbox e o hook gravava UMA LINHA em item_cancelamentos por
  // motivo marcado. Fui conferir os 4.686 cancelamentos de 2026: TODOS têm
  // exatamente 1 motivo — ninguém nunca usou a multi-seleção. O que ela fazia
  // era abrir a porta pra um cancelamento virar 2 ou 3 registros e inflar
  // qualquer contagem por motivo. Escolha única fecha isso.
  //
  // O estado segue array e o hook segue recebendo `motivoIds` — a gravação não
  // muda, só deixa de existir o caso com mais de um.
  const selecionarMotivo = (id: string) => setMotivosSelecionados([id]);

  const handleCancelar = async () => {
    if (!justificativaLiberada) return;

    try {
      await cancelarItem({
        itemId,
        motivoIds: motivosSelecionados,
        statusId: statusSelecionado,
        observacao: justificativa,
      });
    } catch (error) {
      console.error('Erro ao cancelar item:', error);
    } finally {
      setTimeout(() => {
        onOpenChange(false);
        onSuccess?.();
      }, 500);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContentMaxPriority className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            Cancelar Item: {itemNome || 'Produto'}
          </DialogTitle>
          <DialogDescription>
            Passo {step} de 3 - {step === 1 ? 'Tipo de Cancelamento' : step === 2 ? 'Motivo Específico' : 'Justificativa'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* PASSO 1: Escolher Status */}
          {step === 1 && (
            <div>
              <Label className="text-base font-semibold mb-3 block">
                1. Escolha o tipo de cancelamento:
              </Label>
              {emComercial && (
                <div className="mb-4 rounded-lg border border-primary/40 bg-primary/5 p-3 text-sm">
                  <p className="font-medium text-primary">Este item ainda está em negociação</p>
                  <p className="mt-1 text-muted-foreground">
                    Ele não foi para <strong>PEDIDO FECHADO</strong> — ou seja, ainda não virou
                    venda, e não há pedido para cancelar.
                  </p>
                  <ul className="mt-2 list-disc pl-5 text-muted-foreground space-y-0.5">
                    <li>Para trocar produto ou valor: use <strong>Revisar Proposta</strong>.</li>
                    <li>Se o negócio morreu: mova o item para <strong>(0%) NEGOCIAÇÃO CANCELADA</strong>.</li>
                  </ul>
                </div>
              )}
              <RadioGroup value={statusSelecionado} onValueChange={setStatusSelecionado}>
                <div className="space-y-2">
                  {statusCancelamento.map((status: any) => (
                    <div key={status.id} className="flex items-center space-x-3 border rounded-lg p-3 hover:bg-accent cursor-pointer">
                      <RadioGroupItem value={status.id} id={status.id} />
                      <Label htmlFor={status.id} className="cursor-pointer flex-1 flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: status.cor }} />
                        {status.nome}
                      </Label>
                    </div>
                  ))}
                </div>
              </RadioGroup>
              <Button 
                className="w-full mt-4"
                disabled={!statusSelecionado}
                onClick={() => setStep(2)}
              >
                Próximo
              </Button>
            </div>
          )}

          {/* PASSO 2: Escolher Motivo(s) */}
          {step === 2 && (
            <div>
              <Label className="text-base font-semibold mb-3 block">
                2. Escolha o motivo específico:
              </Label>
              <RadioGroup
                value={motivosSelecionados[0] || ''}
                onValueChange={selecionarMotivo}
                className="space-y-2 max-h-[400px] overflow-y-auto pr-2"
              >
                {motivos.map((motivo: any) => (
                  <div
                    key={motivo.id}
                    className={`flex items-start space-x-3 border rounded-lg p-3 hover:bg-accent cursor-pointer ${
                      motivosSelecionados.includes(motivo.id) ? 'border-primary bg-accent' : ''
                    }`}
                    onClick={() => selecionarMotivo(motivo.id)}
                  >
                    <RadioGroupItem value={motivo.id} className="mt-1" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        {motivo.codigo && (
                          <Badge variant="secondary" className="font-mono text-xs">
                            {motivo.codigo}
                          </Badge>
                        )}
                        <span className="font-medium">{motivo.titulo}</span>
                        {motivo.permite_recuperacao ? (
                          <Badge variant="outline" className="text-success border-success">
                            <Clock className="w-3 h-3 mr-1" />
                            Recuperável
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-danger border-danger">
                            <XCircle className="w-3 h-3 mr-1" />
                            Definitivo
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{motivo.descricao}</p>
                      {motivo.permite_recuperacao && (
                        <p className="text-xs text-primary mt-1">
                           Prazo: {motivo.prazo_resolucao_horas}h para resolver
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </RadioGroup>
              {motivos.length === 0 && (
                <p className="text-sm text-muted-foreground border rounded-lg p-4 text-center">
                  Este status não tem motivo cadastrado. Escolha outro ou peça
                  para cadastrarem o motivo em Configurações → Status de Pedidos.
                </p>
              )}
              <div className="flex gap-2 mt-4">
                {!statusPreSelecionado && (
                  <Button variant="outline" onClick={() => setStep(1)}>
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Voltar
                  </Button>
                )}
                <Button 
                  className="flex-1"
                  disabled={motivosSelecionados.length === 0}
                  onClick={() => setStep(3)}
                >
                  Próximo
                </Button>
              </div>
            </div>
          )}

          {/* PASSO 3: Justificativa */}
          {step === 3 && (
            <div>
              <Label className="text-base font-semibold mb-3 block">
                3. Conte o que aconteceu e o que falta para recuperar:
              </Label>
              
              {motivosDetalhes.length > 0 && (
                <div className="mb-4 p-3 bg-muted rounded-lg space-y-2">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertCircle className="w-4 h-4" />
                    <span className="font-medium text-sm">Motivo selecionado:</span>
                  </div>
                  {motivosDetalhes.map((m: any) => (
                    <div key={m.id} className="flex items-center gap-2 text-sm pl-6">
                      <span className="text-muted-foreground">•</span>
                      {m.codigo && (
                        <Badge variant="secondary" className="font-mono text-[10px]">
                          {m.codigo}
                        </Badge>
                      )}
                      <span>{m.titulo}</span>
                      {m.permite_recuperacao ? (
                        <span className="text-success text-xs"> Recuperável ({m.prazo_resolucao_horas}h)</span>
                      ) : (
                        <span className="text-danger text-xs"> Definitivo</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <Textarea
                placeholder="Descreva em detalhes o motivo do cancelamento (mínimo 20 caracteres)..."
                value={justificativa}
                onChange={(e) => setJustificativa(e.target.value)}
                rows={6}
                className="resize-none"
              />
              {/* A sugestão aparece DEPOIS do texto, quando já dá para ler o que
                  aconteceu — e só quando o motivo marcado é outro. Nunca troca
                  sozinha: quem decide é quem está cancelando. (Douglas 04/08) */}
              {motivoSugerido && !motivosSelecionados.some(
                (id) => motivos.find((m: { id: string; titulo: string }) => m.id === id)?.titulo === motivoSugerido
              ) && (
                <div className="mt-2 rounded-lg border border-primary/40 bg-primary/5 p-2.5 text-xs">
                  <p className="text-primary">
                    Pelo que você escreveu, o motivo parece ser{' '}
                    <strong>{motivoSugerido}</strong>.
                  </p>
                  <p className="mt-0.5 text-muted-foreground">
                    Se for isso, volte ao passo 2 e marque — assim o relatório mostra a
                    causa real do cancelamento.
                  </p>
                </div>
              )}
              {/* Conta LETRAS, não caracteres: 49 cancelamentos passaram com
                  "............................" porque 20 caracteres de ponto
                  satisfaziam a regra antiga. (Douglas 04/08) */}
              <p className={`text-xs mt-1 ${!justificativaLiberada ? 'text-destructive' : 'text-muted-foreground'}`}>
                {letrasNaJustificativa < 10
                  ? `Escreva com palavras — faltam ${10 - letrasNaJustificativa} letra(s)`
                  : caracteresNaJustificativa < 20
                    ? `Detalhe um pouco mais — faltam ${20 - caracteresNaJustificativa} caractere(s) (mínimo 20)`
                    : 'Pode enviar'}
              </p>

              <div className="flex gap-2 mt-4">
                <Button variant="outline" onClick={() => setStep(2)}>
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Voltar
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={handleCancelar}
                  disabled={isCancelando || !justificativaLiberada || emComercial}
                >
                  {isCancelando ? 'Cancelando...' : 'Confirmar Cancelamento'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContentMaxPriority>
    </Dialog>
  );
}
